import React from 'react';
import InfoModal from '@/components/molecules/InfoModal';
import RCCreationForm from '@/components/organisms/RCCreationForm';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { revenueCategoryService } from '@/services/revenue-category.service';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { budgetService } from '@/services/budget.service';
import { RevenueCategoryFormValues } from '@/services/types/revenue-category.types';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';

const RCAddPage = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maxRevenueValue, setMaxRevenueValue] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { budgetId } = useParams();
  const location = useLocation();
  const defaultRevenueCategory = location.state?.defaultRevenueCategory as any | undefined;

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}/revenue-categories`,
      // This page is always pushed from the RC list page above, so cancelling
      // should pop back to it rather than replace (which would create a
      // duplicate consecutive history entry and break the back button).
      backOnCancel: true
    });

  console.log('Default Revenue Category', defaultRevenueCategory);

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budget',
          path: `/budgets/${budgetId}`
        },
        {
          title: 'Revenue Category Overview',
          path: `/budgets/${budgetId}/revenue-categories`
        },
        {
          title: 'Create Revenue Category',
          path: ''
        }
      ])
    );
  }, [dispatch, budgetId]);

  useEffect(() => {
    fetchBudgetDetails();
  }, [budgetId]);

  const fetchBudgetDetails = async () => {
    if (!budgetId) {
      setError('Budget ID not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data } = await budgetService.getBudget(budgetId);

      // For revenue categories, the max revenue value should be the remaining BTRT
      setMaxRevenueValue(data.btrt - (data?.btrt_achieved || 0));
    } catch (err) {
      console.error('Error fetching budget:', err);
      setError('Failed to load budget details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: RevenueCategoryFormValues) => {
    console.log('Submit', data);
    try {
      if (!budgetId) {
        throw new Error('BudgetId is required');
      }

      const response = await revenueCategoryService.createRevenueCategory(budgetId, {
        name: data.name,
        description: data.description,
        rcrt: Number(data.rcrt),
        rcrt_alert_threshold: Number(data.rcrt_alert_threshold)
      });

      console.log('Response', response);
      toast.success('Revenue category created successfully');
      allowNavigation();
      // This page was pushed from the RC list page, so pop back to it instead
      // of replacing — replacing to the same URL as the previous history
      // entry would create a duplicate entry and break the back button.
      navigate(-1);
    } catch (error) {
      console.error('Error creating revenue category:', error);
      toast.error('Failed to create revenue category');
    }
  };

  return (
    <>
      <MobileAppBar title="Revenue Category Creation" />

      <div className="bg-[#FBFBFF] p-4 flex flex-col flex-1 md:bg-white rounded-[16px] border">
        <h2 className="text-center w-[80%] mx-auto md:w-full font-semibold mb-5 md:text-left md:text-[24px] md:mb-6">
          Create your Revenue Category
        </h2>

        {/* FORM */}
        <RCCreationForm
          onSubmit={handleSubmit}
          maxRevenueValue={defaultRevenueCategory?.rcrt || maxRevenueValue}
          onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
          onCancel={handleCancel}
        />
      </div>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your revenue category"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}

      {showSuccessModal && (
        <InfoModal
          isOpen={showSuccessModal}
          variant="success"
          title="Revenue Category Created"
          message={<p>You have successfully created a Revenue Category!</p>}
          buttonText="Go to Revenue Categories"
          onButtonClick={() => {
            setShowSuccessModal(false);
            navigate(-1);
          }}
        />
      )}
    </>
  );
};

export default RCAddPage;
